import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";
import { assertNever } from "@impero-com/common/utils/obj";

import { ReportsAllColumn, ReportsAllFilters } from "api/generated/model";
import { PageStateData } from "org/pageState/models";
import { ReportColumnId, REPORT_COLUMN_IDS } from "org/reports/list/columns";

import { decodeOrFilter, encodeOrFilter } from "../common";

import { decodeAccessRights, encodeAccessRights } from "./common";

import { ReportsPageStateRaw } from ".";

type ReportsAllPageStateWithType = PageStateData<"ReportsAll"> & {
    type: "ReportsAll";
};

function encodeReportsAllPageState(
    raw: ReportsPageStateRaw
): ReportsAllPageStateWithType {
    if (raw.pageType !== "ReportsAll") {
        throw new Error(
            `Expected page type to be "ReportsAll", but got "${raw.pageType}"`
        );
    }

    return {
        type: "ReportsAll",
        version: raw.version,
        columns: Array.from(raw.visibleColumnIds).map(encodeReportsAllColumn),
        filters: encodeReportsAllFilters(raw.filter),
        leftSideMenuCollapsed: raw.leftSideMenuCollapsed,
    };
}

function decodeReportsAllPageState(
    input: ReportsAllPageStateWithType
): ReportsPageStateRaw {
    if (input.type !== "ReportsAll") {
        throw new Error(
            `Expected page type to be "ReportsAll", but got "${input.type}"`
        );
    }

    const visibleColumnIdsAsArray = input.columns.map(decodeReportsAllColumn);

    return {
        pageType: "ReportsAll",
        version: input.version,
        visibleColumnIds: new Set(visibleColumnIdsAsArray),
        filter: decodeReportsAllFilters(input.filters),
        leftSideMenuCollapsed: input.leftSideMenuCollapsed,
    };
}

function encodeReportsAllColumn(rawColumn: ReportColumnId): ReportsAllColumn {
    switch (rawColumn) {
        case REPORT_COLUMN_IDS.NAME_FOR_ALL:
            return "name";
        case REPORT_COLUMN_IDS.REPORT_TYPE:
            return "reportType";
        case REPORT_COLUMN_IDS.DESCRIPTION:
            return "description";
        case REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT:
            return "pushReportsCount";
        case REPORT_COLUMN_IDS.ACCESS_RIGHTS:
            return "accessRights";
        case REPORT_COLUMN_IDS.ACTION:
            return "actions";
        case REPORT_COLUMN_IDS.NAME:
        case REPORT_COLUMN_IDS.DATASHEET:
        case REPORT_COLUMN_IDS.DATASHEET_ARCHIVED:
        case REPORT_COLUMN_IDS.CONTROL_PROGRAM:
        case REPORT_COLUMN_IDS.CLASSIFICATION:
            throw new Error(`Unknown column for ReportsAll page: ${rawColumn}`);
        default:
            return assertNever(rawColumn);
    }
}

function decodeReportsAllColumn(column: ReportsAllColumn): ReportColumnId {
    switch (column) {
        case "name":
            return REPORT_COLUMN_IDS.NAME_FOR_ALL;
        case "reportType":
            return REPORT_COLUMN_IDS.REPORT_TYPE;
        case "description":
            return REPORT_COLUMN_IDS.DESCRIPTION;
        case "pushReportsCount":
            return REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT;
        case "accessRights":
            return REPORT_COLUMN_IDS.ACCESS_RIGHTS;
        case "actions":
            return REPORT_COLUMN_IDS.ACTION;
        default:
            return assertNever(column);
    }
}

function encodeReportsAllFilters(
    filter: SimpleAndFilter | null
): ReportsAllFilters {
    if (filter === null) {
        return {};
    }

    const filters = filter.filters;
    return {
        name: encodeOrFilter(REPORT_COLUMN_IDS.NAME_FOR_ALL, filters),
        description: encodeOrFilter(REPORT_COLUMN_IDS.DESCRIPTION, filters),
        pushReportsCount: encodeOrFilter(
            REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT,
            filters
        ),
        reportType: encodeOrFilter(REPORT_COLUMN_IDS.REPORT_TYPE, filters),
        accessRights: encodeAccessRights(filters),
    };
}

function decodeReportsAllFilters(
    filters: ReportsAllFilters
): SimpleAndFilter | null {
    if (Object.keys(filters).length === 0) {
        return null;
    }

    const orFilters: Array<SimpleOrFilter> = [];
    if (filters.name) {
        orFilters.push(
            decodeOrFilter(REPORT_COLUMN_IDS.NAME_FOR_ALL, filters.name)
        );
    }
    if (filters.description) {
        orFilters.push(
            decodeOrFilter(REPORT_COLUMN_IDS.DESCRIPTION, filters.description)
        );
    }
    if (filters.pushReportsCount) {
        orFilters.push(
            decodeOrFilter(
                REPORT_COLUMN_IDS.PUSH_REPORTS_COUNT,
                filters.pushReportsCount
            )
        );
    }
    if (filters.reportType) {
        orFilters.push(
            decodeOrFilter(REPORT_COLUMN_IDS.REPORT_TYPE, filters.reportType)
        );
    }
    if (filters.accessRights) {
        const accessRightFilters = decodeAccessRights(filters.accessRights);
        orFilters.push(...accessRightFilters);
    }

    return {
        filters: orFilters,
    };
}

export { encodeReportsAllPageState, decodeReportsAllPageState };

export type { ReportsAllPageStateWithType };
